'use client'

import React, { useEffect, useState, useCallback } from 'react'
import { DialogRoot } from '@chakra-ui/react'
import Popup from './_components'

interface Popup {
  id: string
  show_after: number
  duration: number
  [key: string]: any
}

interface PopupDisplayerProps {
  popups: Popup[]
}

const PopupDisplayer: React.FC<PopupDisplayerProps> = ({ popups }) => {
  const [activePopup, setActivePopup] = useState<Popup | null>(null)
  const [showPopup, setShowPopup] = useState(false)

  const getClosedPopups = useCallback(() => {
    return JSON.parse(sessionStorage.getItem('closed') || '[]') as string[]
  }, [])

  const saveClosedPopup = useCallback(
    (popupId: string) => {
      const closedPopups = getClosedPopups()
      sessionStorage.setItem(
        'closed',
        JSON.stringify([...closedPopups, popupId])
      )
    },
    [getClosedPopups]
  )

  const getNextPopup = useCallback(() => {
    const closedPopups = getClosedPopups()
    return popups?.find((popup) => !closedPopups.includes(popup.id)) || null
  }, [getClosedPopups, popups])

  useEffect(() => {
    if (!activePopup || !showPopup) {
      const nextPopup = getNextPopup()
      if (nextPopup) {
        setActivePopup(nextPopup)
        setShowPopup(false)

        const showTimeout = setTimeout(() => {
          setShowPopup(true)
        }, nextPopup.show_after * 1000)

        return () => clearTimeout(showTimeout)
      }
    }
  }, [activePopup, showPopup, getNextPopup])

  useEffect(() => {
    if (activePopup && showPopup && activePopup.duration > 0) {
      const durationTimeout = setTimeout(() => {
        handleClosePopup()
      }, activePopup.duration * 1000)

      return () => clearTimeout(durationTimeout)
    }
  }, [activePopup, showPopup])

  const handleClosePopup = () => {
    if (activePopup) {
      saveClosedPopup(activePopup.id)
      setActivePopup(null)
      setShowPopup(false)
    }
  }

  return (
    <>
      {activePopup && showPopup && (
        <DialogRoot
          open={showPopup}
          onOpenChange={handleClosePopup}
          placement="center"
          motionPreset="slide-in-bottom"
          size="xl"
        >
          <Popup activePopup={activePopup} onClose={handleClosePopup} />
        </DialogRoot>
      )}
    </>
  )
}

export default PopupDisplayer
